import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { UserPlus, AlertCircle } from "lucide-react";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="h-16 w-52 overflow-hidden flex items-center justify-center mx-auto mb-4">
            <img src="https://customer-assets.emergentagent.com/job_doctor-speak-hub/artifacts/thatxx48_Pulse%20Medical%20Media%20Logo%20-%20Smooth%20Blue%20Design.svg" alt="Pulse Medical Media" className="h-[220px] w-auto" style={{marginTop: '20px'}} />
          </div>
          <h1 className="text-3xl font-serif font-bold text-foreground">Create Account</h1>
          <p className="text-sm font-sans text-muted-foreground mt-2">Join Pulse Medical Media</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 border border-slate-200 dark:border-slate-700 p-8 bg-white dark:bg-slate-800">
          {error && (
            <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300 font-sans" data-testid="register-error">
              <AlertCircle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}

          <div className="space-y-2">
            <Label className="font-sans text-sm">Full Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Dr. John Doe"
              required
              data-testid="register-name"
            />
          </div>

          <div className="space-y-2">
            <Label className="font-sans text-sm">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
              required
              data-testid="register-email"
            />
          </div>

          <div className="space-y-2">
            <Label className="font-sans text-sm">Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min 6 characters"
              required
              data-testid="register-password"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#1E88E5] hover:bg-[#1565C0] font-sans h-11"
            data-testid="register-submit"
          >
            {loading ? "Creating account..." : <><UserPlus className="h-4 w-4 mr-2" /> Create Account</>}
          </Button>

          <p className="text-center text-sm font-sans text-muted-foreground pt-2">
            Already have an account?{" "}
            <Link to="/login" className="text-[#1E88E5] hover:underline" data-testid="login-link">Sign In</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
